/**
 * CelebrationPage Component
 *
 * A celebratory page with fireworks animation and contact information
 * shown after successful email sending
 */

import React, { useEffect, useState } from "react";

interface CelebrationPageProps {
  onPreviousPage?: () => void;
}

export function CelebrationPage({ onPreviousPage }: CelebrationPageProps) {
  const [fireworks, setFireworks] = useState<
    Array<{
      id: string;
      x: number;
      y: number;
      color: string;
      delay: number;
    }>
  >([]);

  // Generate simple fireworks
  useEffect(() => {
    const colors = [
      "#ff6b6b",
      "#4ecdc4",
      "#45b7d1",
      "#96ceb4",
      "#feca57",
      "#ff9ff3",
      "#54a0ff",
      "#ff4757",
      "#2ed573",
      "#ffa502",
      "#ff6348",
      "#7bed9f",
      "#70a1ff",
      "#5352ed",
    ];

    const generateConfetti = () => {
      const newConfetti = [];

      // Generate confetti pieces falling from the sides
      for (let piece = 0; piece < 20; piece++) {
        // Randomly choose left or right side
        const side = Math.random() < 0.5 ? -1 : 1; // -1 for left, 1 for right

        // Position confetti on the left and right sides where there's no text
        const leftSideX = 15 + Math.random() * 10; // Left side (15-25%)
        const rightSideX = 75 + Math.random() * 10; // Right side (75-85%)
        const x = side === -1 ? leftSideX : rightSideX;

        // Start from top of screen
        const y = 0; // Start at top of screen

        // Random confetti shapes
        const shapes = ["circle", "square", "triangle", "star"];
        const shape = shapes[Math.floor(Math.random() * shapes.length)];

        newConfetti.push({
          id: `confetti-${piece}`,
          x: Math.max(0, Math.min(100, x)),
          y: y,
          color: colors[Math.floor(Math.random() * colors.length)],
          delay: Math.random() * 2,
          shape: shape,
        });
      }

      setFireworks(newConfetti);
    };

    generateConfetti();
    const interval = setInterval(generateConfetti, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <>
      <style>{`
        @keyframes confetti-fall {
          0% {
            transform: translateY(0) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(100vh) rotate(360deg);
            opacity: 0;
          }
        }
        
        @keyframes confetti-wiggle {
          0%, 100% {
            transform: translateX(0) rotate(0deg);
          }
          25% {
            transform: translateX(-5px) rotate(5deg);
          }
          75% {
            transform: translateX(5px) rotate(-5deg);
          }
        }
        
        @keyframes hero-letter-bounce {
          0%, 100% {
            transform: translateY(0) scale(1);
          }
          50% {
            transform: translateY(-10px) scale(1.1);
          }
        }
        
        @keyframes hero-glow {
          0%, 100% {
            text-shadow: 0 0 10px rgba(255, 255, 255, 0.5);
          }
          50% {
            text-shadow: 0 0 20px rgba(255, 255, 255, 0.8), 0 0 30px rgba(59, 130, 246, 0.6);
          }
        }
        
        
        .confetti-fall {
          animation: confetti-fall 4s linear infinite;
        }
        
        .confetti-wiggle {
          animation: confetti-wiggle 0.5s ease-in-out infinite;
        }
        
        .hero-letter-bounce {
          animation: hero-letter-bounce 2s ease-in-out infinite;
        }
        
        .hero-glow {
          animation: hero-glow 3s ease-in-out infinite;
        }
      `}</style>

      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 relative overflow-hidden">
        {/* Confetti Animation */}
        <div className="absolute inset-0 pointer-events-none z-20">
          {fireworks.map((confetti) => (
            <div
              key={confetti.id}
              className="absolute confetti-fall"
              style={{
                left: `${confetti.x}%`,
                top: `${confetti.y}%`,
                animationDelay: `${confetti.delay}s`,
                zIndex: 20,
                width: "10px",
                height: "10px",
                backgroundColor: confetti.color,
                borderRadius: "50%",
                boxShadow: `0 0 10px ${confetti.color}`,
              }}
            />
          ))}
        </div>

        {/* Main Content */}
        <div className="relative z-10 flex flex-col items-center justify-center min-h-screen p-8">
          <div className="text-center max-w-4xl mx-auto">
            {/* HERO Animation */}
            <div className="mb-12">
              <div className="mb-8">
                <div className="flex justify-center items-center space-x-2 mb-4">
                  <span
                    className="text-6xl font-black hero-letter-bounce hero-glow text-white"
                    style={{ animationDelay: "0s" }}
                  >
                    H
                  </span>
                  <span
                    className="text-6xl font-black hero-letter-bounce hero-glow text-white"
                    style={{ animationDelay: "0.2s" }}
                  >
                    E
                  </span>
                  <span
                    className="text-6xl font-black hero-letter-bounce hero-glow text-white"
                    style={{ animationDelay: "0.4s" }}
                  >
                    R
                  </span>
                  <span
                    className="text-6xl font-black hero-letter-bounce hero-glow text-white"
                    style={{ animationDelay: "0.6s" }}
                  >
                    O
                  </span>
                </div>
              </div>
              <h1 className="text-4xl font-bold text-white mb-4">
                Report Successfully Delivered
              </h1>
              <p className="text-lg text-blue-200 mb-2">
                Your compliance report has been sent to the specified email
                address
              </p>
              <p className="text-sm text-blue-300">
                The report includes comprehensive validation results and
                detailed analysis
              </p>
            </div>

            {/* Contact Information */}
            <div className="bg-white/5 backdrop-blur-md rounded-3xl p-8 border border-white/10 mb-8 shadow-2xl">
              <h2 className="text-2xl font-semibold text-white mb-8 text-center">
                Support & Contact Information
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="text-center p-6 bg-white/5 rounded-2xl border border-white/10 hover:bg-white/10 transition-all duration-300">
                  <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <svg
                      className="w-6 h-6 text-blue-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                  <h3 className="text-white font-medium mb-2">
                    Technical Support
                  </h3>
                  <a
                    href="mailto:elad@co-assumption.com"
                    className="text-blue-300 hover:text-blue-200 text-sm transition-colors"
                  >
                    elad@herocon.ai
                  </a>
                </div>

                <div className="text-center p-6 bg-white/5 rounded-2xl border border-white/10 hover:bg-white/10 transition-all duration-300">
                  <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <svg
                      className="w-6 h-6 text-purple-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9v-9m0-9v9"
                      />
                    </svg>
                  </div>
                  <h3 className="text-white font-medium mb-2">
                    Contact for project expansion
                  </h3>
                  <a
                    href="mailto:alon@co-assumption.com"
                    className="text-blue-300 hover:text-blue-200 text-sm transition-colors"
                  >
                    alon@herocon.ai
                  </a>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {onPreviousPage && (
                <button
                  onClick={onPreviousPage}
                  className="px-8 py-3 bg-white/10 hover:bg-white/20 text-white font-medium rounded-xl transition-all duration-300 border border-white/20 hover:border-white/40 backdrop-blur-sm"
                >
                  ← Return to Application
                </button>
              )}

              <button
                onClick={() => window.location.reload()}
                className="px-8 py-3 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-medium rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                Generate New Report
              </button>
            </div>

            {/* Additional Info */}
            <div className="mt-12 text-center">
              <p className="text-blue-200 text-sm mb-2">
                Thank you for using HeroCon.AI for your compliance validation
                needs
              </p>
              <p className="text-blue-300 text-xs">
                Your report has been securely delivered and is ready for review
              </p>
              <div className="mt-4 flex items-center justify-center gap-2 text-xs text-blue-400">
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                  />
                </svg>
                <span>Secure & Encrypted Delivery</span>
              </div>
            </div>
          </div>
        </div>

        {/* Subtle Corner Accents */}
        <div className="absolute top-10 left-10 w-2 h-2 bg-blue-400/30 rounded-full professional-pulse"></div>
        <div className="absolute top-20 right-20 w-1 h-1 bg-green-400/40 rounded-full subtle-glow"></div>
        <div className="absolute bottom-20 left-20 w-1.5 h-1.5 bg-purple-400/30 rounded-full professional-pulse"></div>
        <div className="absolute bottom-10 right-10 w-2 h-2 bg-blue-400/20 rounded-full subtle-glow"></div>
      </div>
    </>
  );
}
